import { Brain, History, Lightbulb, MessageSquareText, Search, Shapes, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";

const memoryReferences = (type, item) => {
  if (!item) return [];
  const refs = [];
  if (item.source) refs.push(`Quelle: ${item.source}`);
  if (item.vault_section) refs.push(`Vault: ${item.vault_section}`);
  if (item.project_id) refs.push(`Projekt: ${item.project_id}`);
  if (item.goal_id) refs.push(`Ziel: ${item.goal_id}`);
  (item.decision_ids || []).forEach((id) => refs.push(`Entscheidung: ${id}`));
  (item.relevant_memory_ids || []).forEach((id) => refs.push(`Memory: ${id}`));
  (item.linked_note_ids || []).forEach((id) => refs.push(`Notiz: ${id}`));
  if (type === "topic") (item.references || []).forEach((ref) => refs.push(`Referenz: ${ref}`));
  (item.tags || []).forEach((tag) => refs.push(`#${tag}`));
  return [...new Set(refs.filter(Boolean))].slice(0, 20);
};

export const normalizeMemoryItem = (type, item) => {
  if (!item) return null;
  const references = memoryReferences(type, item);
  const meta = {
    references,
    createdAt: item.created_at || item.processed_at || item.recorded_at || item.generated_at || null,
    updatedAt: item.updated_at || null,
  };
  if (type === "conversation") return { title: item.message, subtitle: `${item.role === "assistant" ? "AION" : item.role === "user" ? "Du" : "Gespräch"} · ${item.language || "de"}`, body: item.message, ...meta };
  if (type === "topic") return { title: item.name, subtitle: `${item.topic_type} · ${Math.round((item.confidence || 0) * 100)}%`, body: (item.references || []).join(", ") || "Kein Referenztext hinterlegt.", ...meta };
  if (type === "summary") return { title: item.title, subtitle: item.summary_type || "Zusammenfassung", body: item.summary, ...meta };
  if (type === "decision") return { title: item.title, subtitle: item.review_status === "open" ? "Offen" : item.review_status || "Entscheidung", body: item.summary || "Keine Zusammenfassung hinterlegt.", ...meta };
  if (type === "project") return { title: item.title, subtitle: `${item.status || "planned"} · ${Number(item.progress || 0)}% · ${item.priority || "medium"}`, body: item.summary || item.next_action || "Keine Projektzusammenfassung hinterlegt.", ...meta };
  return { title: item.title || item.summary || item.type || "Erinnerung", subtitle: item.classification || item.type || "Memory", body: item.summary || item.text || JSON.stringify(item), ...meta };
};

const renderMemoryList = (items, type, selected, onSelect, testId, emptyTitle, emptyText) => {
  if (!items?.length) {
    return <AionEmptyState title={emptyTitle} description={emptyText} testId={`${testId}-empty`} />;
  }
  return (
    <div className="aion-memory-list" data-testid={`${testId}-list`}>
      {items.map((item, index) => {
        const normalized = normalizeMemoryItem(type, item);
        const itemId = item.id || `${type}-${index}`;
        const isSelected = selected?.id === itemId;
        return (
          <button type="button" className={`aion-memory-card${isSelected ? " aion-memory-card-active" : ""}`} key={itemId} onClick={() => onSelect({ id: itemId, type, raw: item, ...normalized })} data-testid={`${testId}-item-${index}`}>
            <strong>{normalized.title}</strong>
            <small>{normalized.subtitle}</small>
          </button>
        );
      })}
    </div>
  );
};

export const MemoryView = ({
  activeProfile,
  captures,
  decisions,
  projects,
  conversationHistory,
  memory,
  memoryRecallResult,
  memorySearchQuery,
  memorySearchResult,
  memoryTopics,
  onMemorySearch,
  onMemorySearchQueryChange,
  onOpenConversations,
  onOpenDecisions,
  onOpenProjects,
  onSelectMemoryItem,
  selectedMemoryItem,
}) => {
  const topics = memoryTopics?.topics || [];
  const summaries = memoryTopics?.personal_context?.summaries || [];
  const recent = memory?.recent_context || [];
  const conversations = memoryRecallResult?.memories || [];
  const ideas = (captures || []).filter((item) => item.classification === "IDEA");
  const insights = (captures || []).filter((item) => item.classification === "INSIGHT");

  return (
    <div className="aion-view aion-memory-grid" data-testid="memory-view">
      <AionSectionCard title="Memory" eyebrow="ERINNERUNGEN" subtitle="Suche, Themen, Entscheidungen, Ideen, Insights und Gesprächsspeicher auf einer echten Oberfläche." testId="memory-hero-card">
        <div className="aion-status-badge" data-testid="memory-profile-context-badge">
          <span data-testid="memory-profile-context-name">Profil: {activeProfile?.name || "Ich"}</span>
          <small data-testid="memory-profile-context-mode">{activeProfile?.access_mode === "shared_view" ? "Nur freigegebene Familien-Erinnerungen" : "Volle private Ansicht"}</small>
        </div>
        <div className="aion-search-row" data-testid="memory-search-row">
          <Input value={memorySearchQuery} onChange={(event) => onMemorySearchQueryChange(event.target.value)} placeholder="Erinnerungen durchsuchen" data-testid="memory-search-input" />
          <Button onClick={onMemorySearch} disabled={!memorySearchQuery.trim()} data-testid="memory-search-button"><Search className="aion-button-icon" aria-hidden="true" />Suchen</Button>
        </div>
      </AionSectionCard>

      <AionSectionCard
        title="Was AION über dich merkt"
        eyebrow="TRANSPARENZ"
        subtitle="Nur bereits gespeicherte Memory-Daten aus deinem aktuellen Profil — keine versteckten Profilinformationen."
        testId="memory-transparency-card"
      >
        <div className="aion-inline-stats" data-testid="memory-transparency-counts">
          <div className="aion-inline-stat"><span>Fakten</span><strong>{memory?.facts?.length || 0}</strong></div>
          <div className="aion-inline-stat"><span>Präferenzen</span><strong>{memory?.preferences?.length || 0}</strong></div>
          <div className="aion-inline-stat"><span>Favoriten</span><strong>{(memory?.favorite_devices?.length || 0) + (memory?.favorite_scenes?.length || 0)}</strong></div>
          <div className="aion-inline-stat"><span>Automationen</span><strong>{memory?.personalized_automations?.length || 0}</strong></div>
        </div>

        <div className="aion-two-column" data-testid="memory-transparency-grid">
          <div className="aion-note" data-testid="memory-known-facts">
            <strong>Gespeicherte Fakten</strong>
            {(memory?.facts || []).length
              ? (memory.facts || []).slice(0, 12).map((item, index) => <p key={`fact-${index}`}>• {item}</p>)
              : <p>Noch keine expliziten Fakten gespeichert.</p>}
          </div>

          <div className="aion-note" data-testid="memory-known-preferences">
            <strong>Präferenzen</strong>
            {(memory?.preferences || []).length
              ? (memory.preferences || []).slice(0, 12).map((item, index) => <p key={`preference-${index}`}>• {item}</p>)
              : <p>Noch keine Präferenzen gespeichert.</p>}
          </div>

          <div className="aion-note" data-testid="memory-known-favorites">
            <strong>Favoriten</strong>
            {(memory?.favorite_devices || []).map((item, index) => <p key={`device-${index}`}>• Gerät: {item}</p>)}
            {(memory?.favorite_scenes || []).map((item, index) => <p key={`scene-${index}`}>• Szene: {item}</p>)}
            {!(memory?.favorite_devices || []).length && !(memory?.favorite_scenes || []).length ? <p>Noch keine Favoriten gespeichert.</p> : null}
          </div>

          <div className="aion-note" data-testid="memory-known-automation-language">
            <strong>Automationen & Sprache</strong>
            {(memory?.personalized_automations || []).slice(0, 10).map((item, index) => <p key={`automation-${index}`}>• Automation: {item}</p>)}
            <p>• Primärsprache: {memory?.preferred_language || "nicht festgelegt"}</p>
            <p>• Freigegebene Sprachen: {(memory?.preferred_languages || []).length || 0}</p>
          </div>
        </div>
      </AionSectionCard>

      <AionSectionCard title="Suche" eyebrow="SUCHE" subtitle="Treffer aus Gesprächsspeicher, Themen und Zusammenfassungen." testId="memory-search-results-card">
        {memorySearchResult ? (
          <div className="aion-memory-search-results" data-testid="memory-search-results-grid">
            {renderMemoryList(memorySearchResult.conversations || [], "conversation", selectedMemoryItem, onSelectMemoryItem, "memory-search-conversations", "Keine Gesprächstreffer.", "Zu deiner Suche wurden keine Gesprächserinnerungen gefunden.")}
            {renderMemoryList(memorySearchResult.topics || [], "topic", selectedMemoryItem, onSelectMemoryItem, "memory-search-topics", "Keine Themen-Treffer.", "Zu deiner Suche wurden keine Themen gefunden.")}
            {renderMemoryList(memorySearchResult.summaries || [], "summary", selectedMemoryItem, onSelectMemoryItem, "memory-search-summaries", "Keine Zusammenfassungs-Treffer.", "Zu deiner Suche wurden keine Zusammenfassungen gefunden.")}
          </div>
        ) : (
          <AionEmptyState icon={Search} title="Suche starten" description="Gib oben einen Begriff ein, um Gesprächsspeicher, Themen und Zusammenfassungen zu durchsuchen." testId="memory-search-results-empty" />
        )}
      </AionSectionCard>

      <AionSectionCard title="Zuletzt gespeichert" eyebrow="AKTUELL" subtitle="Zuletzt gespeicherter Kontext aus deinem Memory-System." testId="memory-recent-card">
        {renderMemoryList(recent, "recent", selectedMemoryItem, onSelectMemoryItem, "memory-recent", "Noch keine aktuellen Erinnerungen.", "AION zeigt hier echte zuletzt gespeicherte Kontexte an.")}
      </AionSectionCard>

      <AionSectionCard title="Themen" eyebrow="THEMEN" subtitle="Themen, Projekte, Personen, Ideen und Aufgaben aus deiner Nutzung." testId="memory-topics-card">
        {renderMemoryList(topics, "topic", selectedMemoryItem, onSelectMemoryItem, "memory-topics", "Noch keine Themen.", "AION hat aktuell noch keine Themen aus deinen Erinnerungen extrahiert.")}
      </AionSectionCard>

      <AionSectionCard
        title="Projekte"
        eyebrow="PROJEKTE"
        subtitle="Aktive Projekte mit Status sowie Decision- und Memory-Verknüpfungen."
        action={<Button size="sm" variant="outline" onClick={() => onOpenProjects?.()} data-testid="memory-open-projects">Projekt-Workspace</Button>}
        testId="memory-projects-card"
      >
        {renderMemoryList(projects || [], "project", selectedMemoryItem, onSelectMemoryItem, "memory-projects", "Noch keine Projekte.", "Sobald Projekte im Planning-System vorliegen, erscheinen sie hier mit ihren Referenzen.")}
      </AionSectionCard>

      <AionSectionCard
        title="Entscheidungen"
        eyebrow="ENTSCHEIDUNGEN"
        subtitle="Echte Entscheidungs-Einträge mit Review-Status."
        action={<Button size="sm" variant="outline" onClick={() => onOpenDecisions?.()} data-testid="memory-open-decisions">Decision Review</Button>}
        testId="memory-decisions-card"
      >
        {renderMemoryList(decisions, "decision", selectedMemoryItem, onSelectMemoryItem, "memory-decisions", "Noch keine Entscheidungen.", "Sobald echte Entscheidungs-Einträge vorliegen, erscheinen sie hier.")}
      </AionSectionCard>

      <AionSectionCard title="Ideen" eyebrow="IDEEN" subtitle="Capture-Einträge mit IDEA-Klassifikation." testId="memory-ideas-card">
        {renderMemoryList(ideas, "capture", selectedMemoryItem, onSelectMemoryItem, "memory-ideas", "Noch keine Ideen.", "AION zeigt hier nur echte IDEA-Captures an.")}
      </AionSectionCard>

      <AionSectionCard title="Insights" eyebrow="INSIGHTS" subtitle="Capture-Einträge mit INSIGHT-Klassifikation." testId="memory-insights-card">
        {renderMemoryList(insights, "capture", selectedMemoryItem, onSelectMemoryItem, "memory-insights", "Noch keine Insights.", "AION zeigt hier nur echte INSIGHT-Captures an.")}
      </AionSectionCard>

      <AionSectionCard
        title="Gesprächsverlauf"
        eyebrow="HISTORY"
        subtitle="Vergangene AION-Sessions aus deinem bestehenden Gesprächsspeicher."
        action={<Button size="sm" variant="outline" onClick={() => onOpenConversations?.()} data-testid="memory-open-conversations">Gespräche öffnen</Button>}
        testId="memory-history-card"
      >
        {conversationHistory?.length ? (
          <div className="aion-memory-list" data-testid="memory-history-list">
            {conversationHistory.map((session, index) => (
              <button
                type="button"
                className="aion-memory-card"
                key={session.session_id}
                onClick={() => onSelectMemoryItem({
                  id: `history-${session.session_id}`,
                  type: "conversation_history",
                  title: session.preview || `Gespräch ${index + 1}`,
                  subtitle: `${session.message_count || 0} Nachrichten · ${session.language || "de"}`,
                  body: (session.messages || []).map((message) => `${message.role === "assistant" ? "AION" : message.role === "user" ? "Du" : "System"}: ${message.message}`).join("\n\n"),
                  createdAt: session.messages?.[0]?.timestamp || session.last_active || null,
                  updatedAt: session.last_active || null,
                  references: [`Session: ${session.session_id}`],
                  raw: session,
                })}
                data-testid={`memory-history-item-${index}`}
              >
                <strong>{session.preview || `Gespräch ${index + 1}`}</strong>
                <small>{session.message_count || 0} Nachrichten · {session.language || "de"}</small>
              </button>
            ))}
          </div>
        ) : (
          <AionEmptyState icon={MessageSquareText} title="Noch kein Gesprächsverlauf." description="Gespeicherte AION-Sessions erscheinen hier automatisch." testId="memory-history-empty" />
        )}
      </AionSectionCard>

      <AionSectionCard title="Gesprächserinnerungen" eyebrow="GESPRÄCHE" subtitle="Aktuelle Session-Erinnerungen aus dem bestehenden Gesprächsspeicher." testId="memory-conversation-card">
        {renderMemoryList(conversations, "conversation", selectedMemoryItem, onSelectMemoryItem, "memory-conversation", "Noch keine Session-Erinnerungen.", "Sobald du mit AION sprichst, erscheinen hier Gesprächserinnerungen.")}
      </AionSectionCard>

      <AionSectionCard title="Zusammenfassungen" eyebrow="ZUSAMMENFASSUNGEN" subtitle="Vorhandene Memory-Zusammenfassungen aus dem bestehenden Backend." testId="memory-summaries-card">
        {renderMemoryList(summaries, "summary", selectedMemoryItem, onSelectMemoryItem, "memory-summaries", "Noch keine Zusammenfassungen.", "AION hat aktuell noch keine gespeicherten Zusammenfassungen für dich.")}
      </AionSectionCard>

      <AionSectionCard title="Details" eyebrow="DETAILS" subtitle="Eine ausgewählte Erinnerung wird hier im Detail gezeigt." testId="memory-detail-card">
        {selectedMemoryItem ? (
          <div className="aion-memory-detail" data-testid="memory-detail-panel">
            <div className="aion-memory-detail-header" data-testid="memory-detail-header">
              <strong>{selectedMemoryItem.title}</strong>
              <span>{selectedMemoryItem.subtitle}</span>
            </div>
            <p data-testid="memory-detail-body">{selectedMemoryItem.body}</p>
            {(selectedMemoryItem.createdAt || selectedMemoryItem.updatedAt) ? (
              <small data-testid="memory-detail-timestamps">
                {selectedMemoryItem.createdAt ? `Gespeichert: ${selectedMemoryItem.createdAt}` : ""}
                {selectedMemoryItem.updatedAt ? ` · Aktualisiert: ${selectedMemoryItem.updatedAt}` : ""}
              </small>
            ) : null}
            {selectedMemoryItem.references?.length ? (
              <div className="aion-memory-reference-list" data-testid="memory-detail-references">
                <strong>Referenzen & Herkunft</strong>
                {selectedMemoryItem.references.map((reference) => <span key={reference}>{reference}</span>)}
              </div>
            ) : null}
          </div>
        ) : (
          <AionEmptyState icon={Brain} title="Wähle eine Erinnerung aus." description="Klicke auf einen Eintrag in Suche, Aktuell, Themen, Entscheidungen oder Gesprächserinnerungen." testId="memory-detail-empty" />
        )}
      </AionSectionCard>
    </div>
  );
};